import type { ReferenceOption } from "@fieldpath/shared";
import { useEffect, useRef, useState } from "react";

import { clearsChoice, searchNeedle } from "../searchSelect";

/**
 * Choose one thing from a long list by typing.
 *
 * A native select is fine for six options and unusable for 174 clients or
 * 304 Ajera projects, where finding the right one means scrolling a list
 * ordered by a rule you have to infer. Typing three letters is how people
 * expect to do this.
 *
 * Matches anywhere in the label, not just the start: "Buffalograss" should
 * be findable from a project called "Linea Buffalograss Wind", and nobody
 * remembers which word a job was filed under.
 */
export function SearchSelect({
  id,
  options,
  value,
  onChange,
  placeholder,
  disabled,
  emptyLabel,
  ariaLabel,
  inputClassName,
}: {
  /** Optional where an `aria-label` names the field instead of a label. */
  id?: string;
  ariaLabel?: string;
  /** On the INPUT, so a screen's `input.x` rules reach it (the grid's `.open`). */
  inputClassName?: string;
  options: readonly ReferenceOption[];
  value: string;
  onChange: (key: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Shown when nothing is chosen yet. */
  emptyLabel?: string;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  // Whether the text has been changed since the box opened (F191): only then
  // does an empty box mean "remove", and only then does the text filter.
  const [edited, setEdited] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const chosen = options.find((o) => o.key === value) ?? null;
  const needle = searchNeedle(edited, query);
  const matches = needle
    ? options.filter((o) => o.label.toLowerCase().includes(needle)).slice(0, 40)
    : options.slice(0, 40);

  useEffect(() => setHighlight(0), [needle]);

  // Leaving the box: an emptied box clears the choice (F191), anything else
  // keeps it. One function for a click elsewhere, Tab and Enter.
  const leave = (): void => {
    if (clearsChoice(edited, query, value)) onChange("");
    setQuery("");
    setEdited(false);
    setOpen(false);
  };

  // A click anywhere else closes it. Without this the list stays open behind
  // the rest of the form and swallows the next click.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent): void => {
      if (!boxRef.current?.contains(e.target as Node)) leave();
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  });

  const pick = (option: ReferenceOption): void => {
    onChange(option.key);
    setQuery("");
    setEdited(false);
    setOpen(false);
  };

  return (
    <div className="ss" ref={boxRef}>
      <input
        id={id}
        aria-label={ariaLabel}
        className={inputClassName}
        type="text"
        role="combobox"
        aria-expanded={open}
        autoComplete="off"
        disabled={disabled}
        // Opens HOLDING the chosen name, selected, so typing replaces it and
        // deleting it empties the box. Leaving it empty sets "Not set"
        // (F191, Jeremy 2 Nov 2026); the × still clears it too. Clicking in
        // and out without typing changes nothing.
        value={open ? query : (chosen?.label ?? "")}
        placeholder={placeholder ?? emptyLabel}
        onFocus={(e) => {
          setQuery(chosen?.label ?? "");
          setEdited(false);
          setOpen(true);
          e.currentTarget.select();
        }}
        onBlur={() => leave()}
        onChange={(e) => {
          setQuery(e.target.value);
          setEdited(true);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, matches.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === "Enter") {
            // An emptied box means "Not set", never the first name in the list.
            if (clearsChoice(edited, query, value)) {
              e.preventDefault();
              leave();
              return;
            }
            // Only swallow Enter when there is something to choose, or the
            // form could never be submitted from this field.
            const option = matches[highlight];
            if (option) {
              e.preventDefault();
              pick(option);
            }
          } else if (e.key === "Escape") {
            // Escape is "never mind": the choice stays as it was.
            setQuery("");
            setEdited(false);
            setOpen(false);
          }
        }}
      />

      {chosen && !open ? (
        <button
          type="button"
          className="ss-clear"
          title="Clear"
          disabled={disabled}
          onClick={() => onChange("")}
        >
          ×
        </button>
      ) : null}

      {open ? (
        // Keeps focus in the box, so a click on the list (its scrollbar too)
        // never counts as leaving it.
        <ul className="ss-menu" onMouseDown={(e) => e.preventDefault()}>
          {matches.length === 0 ? (
            <li className="ss-none">Nothing matches “{query}”</li>
          ) : (
            matches.map((o, i) => (
              <li key={o.key}>
                <button
                  type="button"
                  className={i === highlight ? "on" : ""}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(o)}
                >
                  {o.label}
                </button>
              </li>
            ))
          )}
          {/* Says when the list is cut short, so a missing name reads as
              "keep typing" rather than "not in the system". */}
          {!needle && options.length > matches.length ? (
            <li className="ss-none">
              {options.length} in total — type to narrow it down
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
